import { Injectable, Injector } from '@angular/core';

import { MediaApiService } from '@pe/checkout/api/media';
import { ProductInterface, ProductTypes } from '@pe/checkout/products';
import { AddressInterface, FlowInterface } from '@pe/checkout/types';
import { PeCurrencyPipe } from '@pe/checkout/utils';

import {
  ShippingAddressInterface,
  ShippingCartProduct,
  ShippingResponseInterface,
  NormilizedShippingInterface,
  WeightUnitEnum,
  DimensionUnitEnum,
} from '../types';

@Injectable()
export class ShippingConverterService {

  private mediaService = this.injector.get(MediaApiService);
  private currencyPipe = this.injector.get(PeCurrencyPipe);

  constructor(protected injector: Injector) {}

  getCartItemIdsFromFlow(flow: FlowInterface): string[] {
    return (flow.cart || []).map(item => item.productId);
  }

  convertFlowAddressToShippingAddress(address: AddressInterface): ShippingAddressInterface {
    const result: ShippingAddressInterface = {
      name: `${address.salutation || ''} ${address.firstName || ''} ${address.lastName || ''}`.trim(),
      streetName: address.streetName || address.street,
      streetNumber: address.streetNumber || '', // TODO address.apartment too ?
      city: address.city,
      stateProvinceCode: '',
      zipCode: address.zipCode,
      countryCode: address.country,
      phone: address.phone || address.mobilePhone,
    };

    return result;
  }

  convertFlowCartToShippingProducts(flow: FlowInterface, products: ProductInterface[]): ShippingCartProduct[] {
    const result: ShippingCartProduct[] = [];
    (flow.cart || []).forEach((cartItem) => {
      const product: ProductInterface = products.find(p =>
        p.id === cartItem.productId
          || !!(p.variants || []).find(pp => pp.id === cartItem.productId)
      );
      if (product && product.shipping && product.type === ProductTypes.Physical) {
        const shippingProduct: ShippingCartProduct = {
          uuid: cartItem.productId,
          productId: cartItem.productId,
          quantity: cartItem.quantity || 0,
          name: cartItem.name || '---',
          image: product.images && product.images.length
            ? this.mediaService.getMediaUrl(product.images[0], 'products')
            : null,
          currency: flow.currency,
          price: cartItem.price || 0,
          weight: parseFloat(product.shipping.weight),
          width: parseFloat(product.shipping.width),
          length: parseFloat(product.shipping.length),
          height: parseFloat(product.shipping.height),
          weightUnit: WeightUnitEnum.kg,
          dimensionUnit: DimensionUnitEnum.cm,
        };
        result.push(shippingProduct);
      }
    });

    return result;
  }

  convertShippingToNormilized(data: ShippingResponseInterface, currency: string): NormilizedShippingInterface[] {
    const result: NormilizedShippingInterface[] = [];
    data.methods.forEach((method) => {
      const desc: string = this.formatDescription(method.rate, currency);
      result.push({
        icon: method.icon,
        title: method.name,
        // title: this.translateService.translate(`checkout_shipping_types.${method.name}.title`),
        description: desc,
        shippingOrderId: data.shippingOrderId,
        integrationSubscriptionId: method.integrationSubscriptionId,
        saveData: {
          // shipping_category: 'custom',
          // shipping_option_name: method.integrationRule.type.name,
          shippingMethodName: method.name,
          shippingFee: method.rate,
        },
      });
    });

    /* // TODO Remove
    if (data.customShipping) {
      const bots = data.customShipping.byOrderTotalsShipping;
      if (bots && bots.isActive && bots.installed) {
        const fee = bots.rateRanges && bots.rateRanges ? bots.rateRanges[0].rate : 0;
        result.push({
          icon: '',
          title: bots.title,
          description: this.formatDescription(fee, currency),
          saveData: {
            shipping_category: 'custom',
            shippingMethodName: 'byOrderTotalsShipping',
            shipping_option_name: 'default',
            shippingFee: fee
          }
        });
      }

      const bws = data.customShipping.byWeightShipping;
      if (bws && bws.isActive && bws.installed) {
        const fee = bws.weightRanges && bws.weightRanges ? bws.weightRanges[0].rate : 0;
        const fromTo = bws.weightRanges && bws.weightRanges ? bws.weightRanges[0] : null;
        const fromToText = fromTo ?
          this.translateService.translate('checkout_shipping_edit.delivery_type_labels.weight_from_to', {
            from: this.formatWeight(fromTo.from),
            to: this.formatWeight(fromTo.upTo)
          }) : '';
        result.push({
          icon: '',
          title: bws.title,
          description: this.formatDescription(fee, currency) + (fromToText ? ` (${fromToText})` : ''),
          saveData: {
            shipping_category: 'custom',
            shippingMethodName: 'byWeightShipping',
            shipping_option_name: 'default',
            shippingFee: fee
          }
        });
      }

      const frs = data.customShipping.flatRateShipping;
      if (frs && frs.isActive && frs.installed) {
        result.push({
          icon: '',
          title: frs.title,
          description: this.formatDescription(frs.rate, currency),
          saveData: {
            shipping_category: 'custom',
            shippingMethodName: 'flatRateShipping',
            shipping_option_name: 'default',
            shippingFee: frs.rate
          }
        });
      }

      const fs = data.customShipping.freeShipping;
      if (fs && fs.isActive && fs.installed) {
        result.push({
          icon: '',
          title: fs.title,
          description: this.formatDescription(0, currency),
          saveData: {
            shipping_category: 'custom',
            shippingMethodName: 'freeShipping',
            shipping_option_name: 'default',
            shippingFee: 0
          }
        });
      }
    }

    if (data.integrations && data.integrations.length) {
      forEach(data.integrations, (shipping: ShippingOptionInterface) => {
        const d: NormilizedShippingInterface = {
          icon: shipping.integration.displayOptions.icon,
          title: shipping.integration.displayOptions.title,
          description: null,
          options: []
        };
        forEach(shipping.integrationSettings.deliveryTypes, (deliveryType: ShippingOptionDeliveryTypeInterface) => {
          d.options.push({
            title: deliveryType.title,
            deliveryTimeDays: deliveryType.deliveryTimeDays,
            price: deliveryType.price,
            saveData: {
              shipping_category: 'integration',
              shippingMethodName: shipping.integration.name,
              shipping_option_name: deliveryType.name,
              shippingFee: deliveryType.price
            }
          });
        });
        result.push(d);
      });
    }*/
    return result;
  }

  private formatDescription(price: number, currency: string, translationKey: string = null): string {
    const priceText = this.currencyPipe.transform(price, currency, 'symbol');
    let result = priceText;
    if (translationKey) {
      // TODO: translate elsewhere if translation key gets used
      // result = $localize `:@@${translationKey}:${price}:price:`;
      result = `${translationKey}${price}`;
      // result = this.translateService.translate(translationKey, { price: priceText });
    }

    return result;
  }

  // private formatWeight(value: number): string {
  //   return this.decimalPipe.transform(value, '1.0-2') + WeightUnitEnum.kg;
  // }
}
